'use client';

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { tv } from 'tailwind-variants';
import { Content } from '.generated/Content/ConveyancingCalculator.model';
import { renderFormField } from './renderFormField';
import { TextField } from './TextField';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { useIsMobile } from 'lib/hooks/useIsMobile';

interface PostcodeFieldProps {
  field: Content.ConveyancingCalculator.FieldDefinition_Item;
  hasError?: boolean;
  reCAPTCHASiteKey?: string;
}

interface AddressItem {
  [key: string]: string | number | undefined;
  AddressLine1?: string;
  AddressLine2?: string;
  AddressLine3?: string;
  Town?: string;
  County?: string;
  Postcode?: string;
  OrganisationName?: string;
  Organisation?: string;
  Street?: string;
}

const postcodeGrid = tv({
  base: ['grid', 'gap-general-spacing-gutter', 'w-full', 'grid-cols-1'],
});

const postcodeFlexContainer = tv({
  base: ['flex', 'flex-col', 'md:flex-row', 'flex-wrap', 'gap-spacing-spacing-12'],
});

const postcodeInputContainer = tv({
  base: [
    'md:flex',
    'md:flex-row',
    'flex-wrap',
    'md:items-start',
    'gap-spacing-spacing-12',
    '[&>div:first-child]:flex-grow',
    'w-full',
    'md:flex-nowrap',
    'postcode-input-container',
  ],
});

const lookupButtonContainer = tv({
  base: ['flex', 'flex-col', 'gap-spacing-spacing-12', 'max-w-[162.08px]', 'md:self-stretch'],
});

const lookupButton = tv({
  base: [
    '!py-spacing-spacing-12',
    '!w-auto',
    'mt-spacing-spacing-12',
    'md:mt-0',
    'whitespace-nowrap',
    '!min-w-[162.08px]',
  ],
});

const addressFieldContainer = tv({
  base: [
    'flex',
    'flex-col',
    'md:flex-row',
    'flex-wrap',
    'md:items-end',
    'gap-spacing-spacing-12',
    '[&>div]:flex-grow',
    'w-full',
  ],
});

const manualEntryButtonContainer = tv({
  base: ['w-full'],
});

const manualEntryButton = tv({
  base: ['!p-0', '!font-normal', 'underline', 'hover:no-underline'],
});

const manualEntryFieldContainer = tv({
  base: ['w-full'],
});

export const PostcodeField: React.FC<PostcodeFieldProps> = ({ field, reCAPTCHASiteKey }) => {
  const fieldConfig = field.fields;
  const isMobile = useIsMobile();
  const postcodeOptions = useMemo(() => {
    const options = fieldConfig?.postcodeOptions;
    if (!options) return [];
    // Handle both single item and array cases
    if (Array.isArray(options)) {
      return options as Content.ConveyancingCalculator.PostcodeDefinition_Item[];
    }
    // If it's a field with targetItems, try to get targetItems
    if (typeof options === 'object' && options !== null && 'targetItems' in options) {
      const targetItems = (options as { targetItems?: unknown }).targetItems;
      if (Array.isArray(targetItems)) {
        return targetItems as Content.ConveyancingCalculator.PostcodeDefinition_Item[];
      }
    }
    return [];
  }, [fieldConfig?.postcodeOptions]);

  // Get Formik context to pass errors and touched state to nested fields
  const {
    errors,
    touched,
    values,
    setValues,
    setFieldTouched,
    setFieldError,
    submitCount,
    isValidating,
  } = useFormikContext<Record<string, string>>();
  const fieldName = field.fields?.fieldName?.value;
  const isTouched = fieldName ? Boolean((touched as Record<string, unknown>)?.[fieldName]) : false;
  const hasError = fieldName
    ? Boolean((errors as Record<string, unknown>)?.[fieldName]) && isTouched
    : false;
  const checkboxFieldName = useMemo(() => {
    const checkboxOption = postcodeOptions.find(
      (option) => option?.fields?.fieldType?.value?.toLowerCase() === 'checkbox'
    );
    return checkboxOption?.fields?.fieldName?.value;
  }, [postcodeOptions]);
  const propertyAddressSuffix = useMemo(() => {
    if (!fieldName) return null;
    return fieldName.startsWith('propertyAddress')
      ? fieldName.replace('propertyAddress', '')
      : null;
  }, [fieldName]);
  const propertyAddressManualFields = useMemo(() => {
    if (!propertyAddressSuffix) return null;
    return {
      line1: `propertyAddressBuildingAndStreet${propertyAddressSuffix}`,
      line2: `propertyAddressTownOrCity${propertyAddressSuffix}`,
      line3: `propertyAddressCounty${propertyAddressSuffix}`,
      postcode: `propertyAddress${propertyAddressSuffix}`,
    };
  }, [propertyAddressSuffix]);
  const propertyAddressTargetsByType = useMemo(() => {
    if (propertyAddressManualFields) {
      return {
        line1: [propertyAddressManualFields.line1],
        line2: [propertyAddressManualFields.line2],
        line3: [propertyAddressManualFields.line3],
        postcode: [propertyAddressManualFields.postcode],
      };
    }
    const allKeys = Object.keys(values);
    const isPropertyAddressKey = (key: string) => key.startsWith('propertyAddress');
    return {
      line1: allKeys.filter(
        (key) => isPropertyAddressKey(key) && key.includes('BuildingAndStreet')
      ),
      line2: allKeys.filter((key) => isPropertyAddressKey(key) && key.includes('TownOrCity')),
      line3: allKeys.filter((key) => isPropertyAddressKey(key) && key.includes('County')),
      postcode: allKeys.filter(
        (key) =>
          isPropertyAddressKey(key) &&
          !key.includes('BuildingAndStreet') &&
          !key.includes('TownOrCity') &&
          !key.includes('County')
      ),
    };
  }, [propertyAddressManualFields, values]);
  const propertyAddressSyncedValuesRef = useRef<Record<string, string>>({});
  const propertyAddressEditedRef = useRef<Record<string, boolean>>({});

  const propertyAddressCheckboxPrevCheckedRef = useRef<boolean>(false);
  const propertyAddressAutoUncheckRef = useRef<boolean>(false);
  const [showManualEntryFields, setShowManualEntryFields] = useState(false);
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const [showAddressList, setShowAddressList] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string>('');
  const [showSuggestedAddressField, setShowSuggestedAddressField] = useState(false);
  // Track if manual entry was just opened to prevent immediate validation
  const manualEntryJustOpenedRef = useRef<boolean>(false);

  // Track previous lookupError to avoid unnecessary updates
  const prevLookupErrorRef = useRef<string>('');
  // Track the last processed select value to prevent infinite loops
  const lastProcessedSelectValueRef = useRef<string | undefined>(undefined);
  // Track if we set a validation error (not a lookup error)
  const validationErrorRef = useRef<boolean>(false);

  // Storage key for this postcode field's addresses
  const storageKey = useMemo(() => {
    return fieldName ? `postcode-addresses-${fieldName}` : null;
  }, [fieldName]);

  // Track previous postcode to detect changes
  const prevPostcodeRef = useRef<string>('');

  const getPostcodeInputContainerHeight = useCallback(() => {
    if (!fieldConfig) return;
    if (typeof window === 'undefined') return;
    const inputContainers = document.querySelectorAll('.postcode-input-container');
    if (inputContainers.length > 0) {
      inputContainers.forEach((element) => {
        const label = element.querySelector('label');
        const lookupButton = element.querySelector('button');
        if (label && lookupButton) {
          if (isMobile) {
            lookupButton.removeAttribute('style');
          } else {
            lookupButton.style.marginTop = `${label.clientHeight + 12}px`;
          }
        }
      });
    }
  }, [fieldConfig, isMobile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    getPostcodeInputContainerHeight();
    window.addEventListener('resize', getPostcodeInputContainerHeight);
    return () => {
      window.removeEventListener('resize', getPostcodeInputContainerHeight);
    };
  }, [getPostcodeInputContainerHeight]);

  // Restore addresses from sessionStorage on mount or when postcode matches
  useEffect(() => {
    if (!storageKey || !fieldName || typeof window === 'undefined') return;

    try {
      const storedData = window.sessionStorage.getItem(storageKey);
      const currentPostcode = (values[fieldName] as string)?.trim() || '';

      // Skip if postcode hasn't changed
      if (prevPostcodeRef.current === currentPostcode) {
        return;
      }

      prevPostcodeRef.current = currentPostcode;

      if (storedData) {
        const parsed = JSON.parse(storedData);
        const storedPostcode = parsed.postcode?.trim() || '';

        // Only restore if postcode matches
        if (
          parsed.addresses &&
          Array.isArray(parsed.addresses) &&
          parsed.addresses.length > 0 &&
          currentPostcode &&
          currentPostcode.toLowerCase() === storedPostcode.toLowerCase()
        ) {
          setAddresses(parsed.addresses);
          setShowAddressList(true);
          setShowSuggestedAddressField(true);
        } else if (
          currentPostcode &&
          currentPostcode.toLowerCase() !== storedPostcode.toLowerCase()
        ) {
          // Clear displayed addresses if postcode doesn't match
          setAddresses([]);
          setShowAddressList(false);
          setShowSuggestedAddressField(false);
        }
      } else if (!currentPostcode) {
        // Clear addresses if no postcode entered
        setAddresses([]);
        setShowAddressList(false);
        setShowSuggestedAddressField(false);
      }
    } catch (error) {
      console.error('Failed to restore addresses from sessionStorage:', error);
    }
  }, [storageKey, fieldName, values]);

  // Store addresses in sessionStorage when they change
  useEffect(() => {
    if (!storageKey || !fieldName || typeof window === 'undefined' || addresses.length === 0)
      return;

    const currentPostcode = (values[fieldName] as string)?.trim() || '';
    if (!currentPostcode) return;

    try {
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          addresses,
          postcode: currentPostcode,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      console.error('Failed to store addresses in sessionStorage:', error);
    }
  }, [addresses, storageKey, fieldName, values]);

  // Set error on postcode field when there's a lookup error
  useEffect(() => {
    if (!fieldName) return;

    // Only update if lookupError actually changed
    if (prevLookupErrorRef.current !== lookupError) {
      if (lookupError) {
        // Set error and mark field as touched
        setFieldError(fieldName, lookupError);
        setFieldTouched(fieldName, true, false);
        validationErrorRef.current = false; // This is a lookup error, not validation error
      } else {
        // Only clear error if it's not a validation error
        // The validation effect will handle clearing validation errors
        if (!validationErrorRef.current) {
          setFieldError(fieldName, undefined);
        }
      }
      prevLookupErrorRef.current = lookupError;
    }
  }, [lookupError, fieldName, setFieldError, setFieldTouched]);

  // Clear manual entry fields
  const clearManualEntryFields = useCallback(() => {
    const updatedValues = { ...values };
    let hasChanges = false;

    postcodeOptions.forEach((postcodeOption) => {
      const optionFieldName = postcodeOption?.fields?.fieldName?.value;
      const hasLabel = postcodeOption?.fields?.label?.value;
      // Clear manual entry fields (fields with label)
      if (hasLabel && optionFieldName && values[optionFieldName]) {
        updatedValues[optionFieldName] = '';
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setValues(updatedValues);
    }
  }, [postcodeOptions, values, setValues]);

  // Handle postcode lookup
  const handleLookup = useCallback(async () => {
    const postcodeValue = fieldName ? (values[fieldName] as string)?.trim() : '';
    if (!postcodeValue) {
      // Only validate and mark touched for this specific postcode field
      if (fieldName) {
        setFieldError(fieldName, 'Please enter a valid postcode');
        setFieldTouched(fieldName, true, false); // false = don't trigger validation on other fields
      }
      setLookupError('Please enter a valid postcode');
      return;
    }

    // Clear manual entry fields when lookup button is clicked
    clearManualEntryFields();

    setIsLoading(true);
    setLookupError('');
    setShowAddressList(false);
    // Reset the processed select value when starting a new lookup
    lastProcessedSelectValueRef.current = undefined;

    const params = new URLSearchParams({ lookup: postcodeValue });

    try {
      const res = await fetch('/api/postcode/PostCode', { method: 'POST', body: params });
      const json = await res.json();
      if (!res.ok) {
        setLookupError(json.error || 'Failed to fetch addresses');
        setIsLoading(false);
        return;
      }

      const items = json?.Item;
      if (!items || (Array.isArray(items) && items.length === 0)) {
        setLookupError('No addresses found for this postcode');
        setIsLoading(false);
        return;
      }

      // Normalize items to array
      const addressList = Array.isArray(items) ? items : [items];
      setAddresses(addressList);
      setShowAddressList(true);
      setShowSuggestedAddressField(true);
      // Reset the processed select value when new addresses are loaded
      lastProcessedSelectValueRef.current = undefined;
    } catch (error) {
      console.error('Postcode lookup error:', error);
      setLookupError('Failed to fetch addresses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [fieldName, values, clearManualEntryFields]);

  // Handle address selection
  const handleAddressSelect = useCallback(
    (address: AddressItem) => {
      // Map address fields to form fields based on postcodeOptions
      const updatedValues = { ...values };

      postcodeOptions.forEach(
        (postcodeOption: Content.ConveyancingCalculator.PostcodeDefinition_Item) => {
          const optionFieldName = postcodeOption?.fields?.fieldName?.value;
          if (!optionFieldName) return;

          // Map common address field names
          // You may need to adjust these mappings based on your actual API response structure
          let addressValue: string | undefined;

          // Try to match field name to address property (case-insensitive)
          const fieldNameLower = optionFieldName.toLowerCase();
          if (fieldNameLower.includes('building') || fieldNameLower.includes('street')) {
            addressValue = address.Organisation
              ? `${address.Organisation}, ${address.Street}`
              : address.Property
                ? `${address.Property}, ${address.Street}`
                : (`${address.Street}` as string | undefined);
          } else if (fieldNameLower.includes('town') || fieldNameLower.includes('city')) {
            addressValue = address.Town as string | undefined;
          } else if (fieldNameLower.includes('county')) {
            addressValue = address.AbbreviatedOptionalCounty as string | undefined;
          } else {
            // Try direct property match
            addressValue = address[optionFieldName] as string | undefined;
          }

          if (addressValue) {
            updatedValues[optionFieldName] = String(addressValue);
          }
        }
      );

      setValues(updatedValues);
      // Keep dropdown visible after selection - don't hide it
      setShowManualEntryFields(true);
    },
    [postcodeOptions, values, setValues]
  );

  // Find the first select field in postcodeOptions to use for address dropdown
  const addressSelectField = useMemo(() => {
    return postcodeOptions.find(
      (option) =>
        option?.fields?.fieldType?.value?.toLowerCase() === 'select' ||
        option?.fields?.fieldType?.value?.toLowerCase() === 'dropdown'
    );
  }, [postcodeOptions]);

  // Create address options for select field (organisation + street)
  const addressSelectOptions = useMemo(() => {
    if (!addresses || addresses.length === 0) return [];
    return addresses.map((address) => {
      const property = address.Property || address.AddressLine2 || '';
      const organisation =
        address.OrganisationName || address.Organisation || address.AddressLine2 || '';
      const street = address.Street || address.AddressLine1 || '';
      const label = organisation
        ? `${organisation}, ${street}`
        : property
          ? `${property}, ${street}`
          : street || '';
      return {
        value: label,
        label: label,
        address: address, // Store full address for later use
      };
    });
  }, [addresses]);

  // Create modified field with address options
  const addressFieldWithOptions = useMemo(() => {
    if (!addressSelectField || addressSelectOptions.length === 0) return null;

    // Create a modified field object with dynamic options
    const modifiedField = {
      ...addressSelectField,
      fields: {
        ...addressSelectField.fields,
        selectOptionsAndSection: addressSelectOptions.map((option) => ({
          name: `option-${option.value}`,
          fields: {
            value: { value: option.value },
            text: { value: option.label },
          },
        })),
      },
    };

    return modifiedField as unknown as Content.ConveyancingCalculator.PostcodeDefinition_Item;
  }, [addressSelectField, addressSelectOptions]);

  // Handle address selection from select field
  const handleAddressSelectFromDropdown = useCallback(
    (selectedValue: string) => {
      // Find the address option that matches the selected value (label)
      const selectedOption = addressSelectOptions.find((opt) => opt.value === selectedValue);
      if (!selectedOption || !selectedOption.address) return;

      // Clear manual entry fields when suggested address is selected
      clearManualEntryFields();

      handleAddressSelect(selectedOption.address);
    },
    [addressSelectOptions, handleAddressSelect, clearManualEntryFields]
  );

  // Keep suggested address dropdown visible if it has a value
  useEffect(() => {
    if (!addressSelectField) return;

    const selectFieldName = addressSelectField.fields?.fieldName?.value;
    if (!selectFieldName) return;

    const selectedValue = values[selectFieldName] as string | undefined;
    const hasValue = selectedValue && selectedValue.trim() !== '';

    if (hasValue) {
      setShowSuggestedAddressField(true);
      setShowAddressList(true);
    }
  }, [values, addressSelectField]);

  // Keep manual entry fields visible if any of them have values
  useEffect(() => {
    const hasManualEntryValues = postcodeOptions.some((postcodeOption) => {
      const optionFieldName = postcodeOption?.fields?.fieldName?.value;
      const hasLabel = postcodeOption?.fields?.label?.value;
      // Check if it's a manual entry field (has label) and has a value
      if (hasLabel && optionFieldName) {
        const fieldValue = values[optionFieldName] as string | undefined;
        return fieldValue && fieldValue.trim() !== '';
      }
      return false;
    });

    if (hasManualEntryValues) {
      setShowManualEntryFields(true);
    }
  }, [values, postcodeOptions]);

  // When checkbox is checked, show property address manual entry fields and copy values
  useEffect(() => {
    if (
      !checkboxFieldName ||
      propertyAddressTargetsByType.postcode.length === 0 ||
      propertyAddressTargetsByType.line1.length === 0 ||
      propertyAddressTargetsByType.line2.length === 0 ||
      propertyAddressTargetsByType.line3.length === 0
    )
      return;

    const isChecked = Boolean((values[checkboxFieldName] as string | undefined)?.trim());
    const sourceFields = {
      line1: 'postcodeBuildingAndStreet',
      line2: 'postcodeTownOrCity',
      line3: 'postcodeCounty',
      postcode: 'postcode',
    };

    if (isChecked) {
      (Object.values(propertyAddressTargetsByType) as string[][]).forEach((targets) => {
        targets.forEach((targetField) => {
          if ((errors as Record<string, unknown>)?.[targetField]) {
            setFieldError(targetField, undefined);
          }
          if ((touched as Record<string, unknown>)?.[targetField]) {
            setFieldTouched(targetField, false, false);
          }
        });
      });
      propertyAddressCheckboxPrevCheckedRef.current = true;
      setShowManualEntryFields(true);
      const updatedValues = { ...values };
      let hasChanges = false;

      (Object.keys(sourceFields) as Array<keyof typeof sourceFields>).forEach((fieldKey) => {
        const sourceValue = (values[sourceFields[fieldKey]] as string | undefined) || '';
        const targets = propertyAddressTargetsByType[fieldKey];
        targets.forEach((targetField) => {
          const currentTargetValue = (updatedValues[targetField] as string | undefined) || '';
          const lastSyncedValue = propertyAddressSyncedValuesRef.current[targetField] || '';

          if (lastSyncedValue !== '' && currentTargetValue !== lastSyncedValue) {
            propertyAddressEditedRef.current[targetField] = true;
          }

          if (
            !propertyAddressEditedRef.current[targetField] &&
            currentTargetValue !== sourceValue
          ) {
            updatedValues[targetField] = sourceValue;
            propertyAddressSyncedValuesRef.current[targetField] = sourceValue;
            hasChanges = true;
          }
        });
      });

      if (hasChanges) {
        setValues(updatedValues);
      }
      return;
    }

    if (propertyAddressCheckboxPrevCheckedRef.current) {
      if (!propertyAddressAutoUncheckRef.current) {
        const updatedValues = { ...values };
        let hasChanges = false;
        (Object.values(propertyAddressTargetsByType) as string[][]).forEach((targets) => {
          targets.forEach((targetField) => {
            if (updatedValues[targetField]) {
              updatedValues[targetField] = '';
              hasChanges = true;
            }
          });
        });
        if (hasChanges) {
          setValues(updatedValues);
        }
      }
      propertyAddressSyncedValuesRef.current = {};
      propertyAddressEditedRef.current = {};
      propertyAddressCheckboxPrevCheckedRef.current = false;
      propertyAddressAutoUncheckRef.current = false;
    }
  }, [checkboxFieldName, propertyAddressTargetsByType, values, setValues]);

  // Uncheck checkbox if user edits any propertyAddress fields while checked
  useEffect(() => {
    if (!checkboxFieldName) return;
    const isChecked = Boolean((values[checkboxFieldName] as string | undefined)?.trim());
    if (!isChecked) return;

    const hasEdited = Object.values(propertyAddressEditedRef.current).some(Boolean);
    if (!hasEdited) return;

    propertyAddressAutoUncheckRef.current = true;
    const updatedValues = { ...values, [checkboxFieldName]: '' };
    setValues(updatedValues);
  }, [checkboxFieldName, values, setValues]);

  // Watch for address select field value changes
  useEffect(() => {
    if (!addressSelectField || !showAddressList) return;

    const selectFieldName = addressSelectField.fields?.fieldName?.value;
    if (!selectFieldName) return;

    const selectedValue = values[selectFieldName] as string | undefined;

    // Only process if:
    // 1. There's a selected value
    // 2. It's a valid option
    // 3. It's different from the last processed value (prevent infinite loop)
    if (
      selectedValue &&
      selectedValue !== lastProcessedSelectValueRef.current &&
      addressSelectOptions.some((opt) => opt.value === selectedValue)
    ) {
      lastProcessedSelectValueRef.current = selectedValue;
      handleAddressSelectFromDropdown(selectedValue);
    }
  }, [
    values,
    addressSelectField,
    addressSelectOptions,
    showAddressList,
    handleAddressSelectFromDropdown,
  ]);

  // Validate postcode field: set error if postcode has value but no suggested address is selected
  useEffect(() => {
    if (!fieldName || !addressSelectField || !storageKey) return;

    const postcodeValue = (values[fieldName] as string)?.trim() || '';
    const selectFieldName = addressSelectField.fields?.fieldName?.value;
    if (!selectFieldName) return;

    // Check if postcode has a value
    const hasPostcode = postcodeValue !== '';

    // Check if addresses are available (either loaded or stored in sessionStorage)
    let hasAddresses = addresses.length > 0;
    if (!hasAddresses && typeof window !== 'undefined') {
      try {
        const storedData = window.sessionStorage.getItem(storageKey);
        if (storedData) {
          const parsed = JSON.parse(storedData);
          const storedPostcode = parsed.postcode?.trim() || '';
          // Check if stored postcode matches current postcode
          if (
            parsed.addresses &&
            Array.isArray(parsed.addresses) &&
            parsed.addresses.length > 0 &&
            postcodeValue &&
            postcodeValue.toLowerCase() === storedPostcode.toLowerCase()
          ) {
            hasAddresses = true;
          }
        }
      } catch (_error) {
        // Ignore storage errors
      }
    }

    // Check if suggested address dropdown has a value
    const selectedAddressValue = (values[selectFieldName] as string)?.trim() || '';

    // Check if any manual entry fields have values
    const hasManualEntryValues = postcodeOptions.some((postcodeOption) => {
      const optionFieldName = postcodeOption?.fields?.fieldName?.value;
      const hasLabel = postcodeOption?.fields?.label?.value;
      // Check if it's a manual entry field (has label) and has a value
      if (hasLabel && optionFieldName) {
        const fieldValue = values[optionFieldName] as string | undefined;
        return fieldValue && fieldValue.trim() !== '';
      }
      return false;
    });

    // Set error on postcode field if postcode has value, addresses are available, but no address is selected
    // Only show error if manual entry fields are also not filled AND manual entry is not shown
    const validationErrorMessage = 'Please click on the lookup button to find the address';
    const currentError = (errors as Record<string, unknown>)?.[fieldName] as string | undefined;
    // Don't show error if manual entry fields are shown (user chose manual entry)
    const shouldShowError =
      hasPostcode && !selectedAddressValue && !hasManualEntryValues && !showManualEntryFields;

    if (shouldShowError) {
      // Set validation error
      setFieldError(fieldName, validationErrorMessage);
      setFieldTouched(fieldName, true, false);
      validationErrorRef.current = true; // Mark that we set a validation error
    } else {
      // Clear error on postcode field only if:
      // - We previously set a validation error, AND
      // - The current error matches our validation error message
      // This ensures we don't clear errors from other sources (like lookup errors)
      if (validationErrorRef.current && currentError === validationErrorMessage) {
        setFieldError(fieldName, undefined);
        validationErrorRef.current = false;
      }
    }
  }, [
    fieldName,
    values,
    errors,
    addressSelectField,
    postcodeOptions,
    addresses.length,
    storageKey,
    showManualEntryFields,
    setFieldError,
    setFieldTouched,
  ]);

  // Validate manual entry fields: validate only touched fields one at a time
  useEffect(() => {
    if (!fieldName || !addressSelectField) return;

    const selectFieldName = addressSelectField.fields?.fieldName?.value;
    if (!selectFieldName) return;

    // Only validate if manual entry fields are shown
    if (!showManualEntryFields) {
      // Reset the flag when manual entry is hidden
      if (manualEntryJustOpenedRef.current) {
        manualEntryJustOpenedRef.current = false;
      }
      return;
    }

    // Reset the flag after manual entry is shown (after initial render)
    if (manualEntryJustOpenedRef.current) {
      manualEntryJustOpenedRef.current = false;
    }

    const shouldValidateAll = submitCount > 0 || isValidating;

    // Validate only touched manual entry fields (or all on submit)
    postcodeOptions.forEach((postcodeOption) => {
      const optionFieldName = postcodeOption?.fields?.fieldName?.value;
      const hasLabel = postcodeOption?.fields?.label?.value;
      if (!hasLabel || !optionFieldName) return;

      const isFieldTouched = Boolean((touched as Record<string, unknown>)?.[optionFieldName]);
      if (!isFieldTouched && !shouldValidateAll) {
        return;
      }

      const fieldValue = (values[optionFieldName] as string)?.trim() || '';
      const fieldLabel = postcodeOption?.fields?.label?.value || optionFieldName;
      const requiredValue = postcodeOption?.fields?.required?.value;
      const isRequired = requiredValue === true || String(requiredValue).toLowerCase() === 'true';
      const requiredMessage =
        postcodeOption?.fields?.requiredValidationMessage?.value || `${fieldLabel} is required`;

      if (!isRequired) {
        const currentError = (errors as Record<string, unknown>)?.[optionFieldName] as
          | string
          | undefined;
        if (currentError && currentError.includes('required')) {
          setFieldError(optionFieldName, undefined);
        }
        return;
      }

      if (!fieldValue) {
        // Field is required but empty - show error
        setFieldError(optionFieldName, requiredMessage);
        if (shouldValidateAll) {
          setFieldTouched(optionFieldName, true, false);
        }
      } else {
        // Clear error if field has value
        const currentError = (errors as Record<string, unknown>)?.[optionFieldName] as
          | string
          | undefined;
        if (currentError && currentError.includes('required')) {
          setFieldError(optionFieldName, undefined);
        }
      }
    });
  }, [
    showManualEntryFields,
    fieldName,
    values,
    errors,
    touched,
    addressSelectField,
    postcodeOptions,
    setFieldError,
    setFieldTouched,
    submitCount,
    isValidating,
  ]);

  // Validate that either suggested address or manual entry fields are filled
  useEffect(() => {
    if (!fieldName || !addressSelectField || !storageKey) return;

    const postcodeValue = (values[fieldName] as string)?.trim() || '';
    const selectFieldName = addressSelectField.fields?.fieldName?.value;
    if (!selectFieldName) return;

    // Check if postcode has a value
    const hasPostcode = postcodeValue !== '';

    // Check if addresses are available (either loaded or stored in sessionStorage)
    let hasAddresses = addresses.length > 0;
    if (!hasAddresses && typeof window !== 'undefined') {
      try {
        const storedData = window.sessionStorage.getItem(storageKey);
        if (storedData) {
          const parsed = JSON.parse(storedData);
          const storedPostcode = parsed.postcode?.trim() || '';
          // Check if stored postcode matches current postcode
          if (
            parsed.addresses &&
            Array.isArray(parsed.addresses) &&
            parsed.addresses.length > 0 &&
            postcodeValue &&
            postcodeValue.toLowerCase() === storedPostcode.toLowerCase()
          ) {
            hasAddresses = true;
          }
        }
      } catch (_error) {
        // Ignore storage errors
      }
    }

    // Check if suggested address dropdown has a value
    const selectedAddressValue = (values[selectFieldName] as string)?.trim() || '';

    // Check if any manual entry fields have values
    const hasManualEntryValues = postcodeOptions.some((postcodeOption) => {
      const optionFieldName = postcodeOption?.fields?.fieldName?.value;
      const hasLabel = postcodeOption?.fields?.label?.value;
      // Check if it's a manual entry field (has label) and has a value
      if (hasLabel && optionFieldName) {
        const fieldValue = values[optionFieldName] as string | undefined;
        return fieldValue && fieldValue.trim() !== '';
      }
      return false;
    });

    // Check if either suggested address is selected OR manual entry fields are filled
    const hasAddressData = selectedAddressValue !== '' || hasManualEntryValues;

    // Validate if postcode has a value and addresses are available
    // This ensures validation happens even if fields are hidden or untouched
    if (hasPostcode && hasAddresses) {
      // Set error if both suggested address and manual entry fields are empty
      if (!hasAddressData) {
        const errorMessage =
          addressSelectField.fields?.requiredValidationMessage?.value ||
          'Please select an address or enter address details manually';
        setFieldError(selectFieldName, errorMessage);
        // Mark field as touched to show the error
        setFieldTouched(selectFieldName, true, false);
      } else {
        // Clear error if address data is present
        setFieldError(selectFieldName, undefined);
      }
    } else {
      // Clear error if conditions aren't met
      setFieldError(selectFieldName, undefined);
    }
  }, [
    fieldName,
    values,
    addressSelectField,
    postcodeOptions,
    addresses.length,
    storageKey,
    setFieldError,
    setFieldTouched,
  ]);

  if (!postcodeOptions || postcodeOptions.length === 0) {
    return null;
  }

  return (
    <div className={postcodeGrid()} data-testid="postcode-grid">
      <div className={postcodeFlexContainer()}>
        <div className={postcodeInputContainer()}>
          <TextField
            key={field.id || 'postcode'}
            field={field}
            hasError={hasError}
            isTouched={isTouched}
          />
          <div className={lookupButtonContainer()}>
            <ButtonWrapper
              text={isLoading ? 'Loading...' : fieldConfig?.lookupButtonText?.value || 'Lookup'}
              ctaComponentClass="default"
              ctaVariant="secondary"
              className={lookupButton()}
              onClick={(e) => {
                // Prevent form submission if button is inside a form
                e?.preventDefault?.();
                // Stop event propagation to prevent triggering validation on other fields
                e?.stopPropagation?.();
                handleLookup();
              }}
              disabled={isLoading}
            />
          </div>
        </div>
        {showSuggestedAddressField &&
          postcodeOptions.map(
            (
              postcodeOption: Content.ConveyancingCalculator.PostcodeDefinition_Item,
              index: number
            ) => {
              if (
                !postcodeOption?.fields?.fieldName?.value ||
                !showSuggestedAddressField ||
                postcodeOption?.fields?.label?.value
              ) {
                return null;
              }

              // Check if this is the address select field and we have addresses
              const isAddressSelectField =
                (postcodeOption?.fields?.fieldType?.value?.toLowerCase() === 'select' ||
                  postcodeOption?.fields?.fieldType?.value?.toLowerCase() === 'dropdown') &&
                addressFieldWithOptions &&
                showAddressList;

              const fieldToRender = isAddressSelectField ? addressFieldWithOptions : postcodeOption;

              return (
                <div
                  key={postcodeOption?.fields?.fieldName?.value || index}
                  className={addressFieldContainer()}
                >
                  {renderFormField(
                    fieldToRender,
                    errors,
                    touched,
                    reCAPTCHASiteKey,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    !postcodeOption?.fields?.label?.value
                  )}
                </div>
              );
            }
          )}
        {postcodeOptions.map(
          (
            postcodeOption: Content.ConveyancingCalculator.PostcodeDefinition_Item,
            index: number
          ) => {
            const isCheckboxField =
              postcodeOption?.fields?.fieldType?.value?.toLowerCase() === 'checkbox';

            if (isCheckboxField) {
              return (
                <div
                  key={postcodeOption?.fields?.fieldName?.value || index}
                  className={manualEntryButtonContainer()}
                >
                  {renderFormField(
                    postcodeOption,
                    errors,
                    touched,
                    reCAPTCHASiteKey,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    true
                  )}
                </div>
              );
            }
            return null;
          }
        )}
        {!showManualEntryFields && (
          <div className={manualEntryButtonContainer()}>
            <ButtonWrapper
              text={fieldConfig?.manualEntryText?.value || 'Enter manually'}
              ctaComponentClass="default"
              ctaVariant="tertiary"
              className={manualEntryButton()}
              onClick={(e) => {
                // Prevent form submission if button is inside a form
                e?.preventDefault?.();
                // Stop event propagation to prevent triggering validation on other fields
                e?.stopPropagation?.();

                // Clear validation error from postcode field when user chooses manual entry
                // This removes "Please click on the lookup button to find the address" error
                if (fieldName) {
                  const postcodeError = (errors as Record<string, unknown>)?.[fieldName] as
                    | string
                    | undefined;
                  const validationErrorMessage =
                    'Please click on the lookup button to find the address';

                  // Clear the error if it matches the validation error message
                  if (postcodeError === validationErrorMessage) {
                    setFieldError(fieldName, undefined);
                    // Reset validation error ref to prevent the validation effect from re-setting it
                    validationErrorRef.current = false;
                  }
                }

                // Clear any existing errors on manual entry fields before showing them
                postcodeOptions.forEach((postcodeOption) => {
                  const optionFieldName = postcodeOption?.fields?.fieldName?.value;
                  const hasLabel = postcodeOption?.fields?.label?.value;
                  if (hasLabel && optionFieldName) {
                    const currentError = (errors as Record<string, unknown>)?.[optionFieldName] as
                      | string
                      | undefined;
                    // Clear any required errors on manual entry fields
                    if (currentError && currentError.includes('required')) {
                      setFieldError(optionFieldName, undefined);
                    }
                  }
                });
                // Set flag to prevent immediate validation when fields are shown
                manualEntryJustOpenedRef.current = true;
                setShowManualEntryFields(true);
              }}
            />
          </div>
        )}
      </div>
      {showManualEntryFields &&
        postcodeOptions.map(
          (
            postcodeOption: Content.ConveyancingCalculator.PostcodeDefinition_Item,
            index: number
          ) => {
            if (
              !postcodeOption?.fields?.fieldName?.value ||
              !postcodeOption?.fields?.label?.value ||
              !showManualEntryFields ||
              postcodeOption?.fields?.fieldType?.value?.toLowerCase() === 'checkbox'
            ) {
              return null;
            }

            return (
              <div
                key={postcodeOption.fields?.fieldName?.value || index}
                className={manualEntryFieldContainer()}
              >
                {renderFormField(
                  postcodeOption,
                  errors,
                  touched,
                  reCAPTCHASiteKey,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  !postcodeOption?.fields?.label?.value
                )}
              </div>
            );
          }
        )}
    </div>
  );
};
